import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { Tooltip, Box, Typography, Stack, InputAdornment, CircularProgress, Alert } from "@mui/material";
import copy from "copy-to-clipboard";

import { ReactComponent as InfoIcon } from "../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as CopyIcon } from "../../../../vendor/_new/icon/sites/copy.svg";

import useSite from "../../../../hooks/user/useSite";
import useSSLConfig from "../../../../hooks/user/useSSLConfig";
import colors from "../../../../utils/colors";
import { SafeTextField } from "../../../common/Greens";
import { CancelButton, SaveButton } from "../../../common/Button";
import { useNotification } from "../../../../contexts/NotificationContext";

const Root = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 200px;
`;

function SSLConfigurationFree({ open, siteUid, handleClose }) {
  const RECORD_TYPE = "CNAME";
  const { cursite } = useSite();
  const { cert_id, cname_validations, generateCerts, verifyDomains, clearWildcardCerts, getSSLConfig, configSslSetting } = useSSLConfig();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [copyTypeSuccess, setCopyTypeSuccess] = useState(false);
  const [copyNameSuccess, setCopyNameSuccess] = useState(false);
  const [copyContentSuccess, setCopyContentSuccess] = useState(false);
  const { showSuccess } = useNotification();

  const handleCopyType = (e) => {
    copy(RECORD_TYPE);
    e.target.focus();
    setCopyTypeSuccess(true);
  };

  const handleCopyName = (e) => {
    copy(cname_validations[0]);
    e.target.focus();
    setCopyNameSuccess(true);
  };

  const handleCopyContent = (e) => {
    copy(cname_validations[1]);
    e.target.focus();
    setCopyContentSuccess(true);
  };

  const handleOnTypeTooltipClose = () => {
    setTimeout(() => {
      setCopyTypeSuccess(false);
    }, 100);
  };
  const handleOnNameTooltipClose = () => {
    setTimeout(() => {
      setCopyNameSuccess(false);
    }, 100);
  };
  const handleOnContentTooltipClose = () => {
    setTimeout(() => {
      setCopyContentSuccess(false);
    }, 100);
  };

  const generate = async () => {
    setErrors(null);
    setLoading(true);
    try {
      await generateCerts(siteUid);
    } catch (error) {
      const message = error.message || "Something went wrong";
      setErrors(message);
    }
    setLoading(false);
  };

  const verify = async () => {
    setErrors(null);
    setLoading(true);
    try {
      await verifyDomains(siteUid, cert_id);
      if (cursite?.id) {
        await getSSLConfig(siteUid);
      }
      await configSslSetting(siteUid, "httpsRedirectChange", { enable: true });
      showSuccess("SSL cert added and verified");
      handleClose();
    } catch (error) {
      const message = error.message || "Something went wrong";
      setErrors(message);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!open) return;
    if (!cname_validations?.length) {
      generate();
    }
    return () => {
      setLoading(false);
      setErrors(null);
      clearWildcardCerts();
      setCopyTypeSuccess(false);
      setCopyNameSuccess(false);
      setCopyContentSuccess(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursite, open]);

  return (
    <Stack spacing={3} justifyContent={"space-between"} sx={{ height: "100%" }}>
      {loading ? (
        <Root>
          <CircularProgress color="primary" />
        </Root>
      ) : (
        <Stack spacing={4}>
          <Stack spacing={1}>
            <Typography
              sx={{
                fontWeight: "medium",
                fontSize: 18,
                lineHeight: "24px",
                color: colors.neutral[1000],
              }}
            >
              Universal
            </Typography>
            <Typography sx={ { fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
              Please add the following CNAME record on your DNS manager and click Verify Domain button.
            </Typography>
          </Stack>
          {errors?.length ? (
            <Alert variant="outlined" severity="error" mt={2} mb={2} sx={ { fontSize: 14, fontWeight: "regular" }}>
              {errors}
            </Alert>
          ) : (
            <></>
          )}
          <SafeTextField
            fullWidth
            variant="outlined"
            label="Type"
            value={RECORD_TYPE}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copyTypeSuccess ? "Copied!" : "Click to Copy"}
                    leaveDelay={copyTypeSuccess ? 500 : 200}
                    onClose={handleOnTypeTooltipClose}
                  >
                    <Box onClick={handleCopyType} sx={{ cursor: "pointer" }}>
                      <CopyIcon style={{ color: colors.neutral[600] }} />
                    </Box>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <SafeTextField
            fullWidth
            variant="outlined"
            label="Name"
            value={cname_validations?.[0] ?? ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copyNameSuccess ? "Copied!" : "Click to Copy"}
                    leaveDelay={copyNameSuccess ? 500 : 200}
                    onClose={handleOnNameTooltipClose}
                  >
                    <Box onClick={handleCopyName} sx={{ cursor: "pointer" }}>
                      <CopyIcon style={{ color: colors.neutral[600] }} />
                    </Box>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <SafeTextField
            fullWidth
            multiline
            minRows={3}
            variant="outlined"
            label="Type"
            value={cname_validations?.[1] ?? ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={copyContentSuccess ? "Copied!" : "Click to Copy"}
                    leaveDelay={copyContentSuccess ? 500 : 200}
                    onClose={handleOnContentTooltipClose}
                  >
                    <Box onClick={handleCopyContent} sx={{ cursor: "pointer" }}>
                      <CopyIcon style={{ color: colors.neutral[600] }} />
                    </Box>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />
          <Stack
            direction={"row"}
            spacing={2}
            sx={{
              minHeight: 48,
              p: 2,
              borderRadius: "8px",
              backgroundColor: colors.primary["alpha"],
              border: `solid 1px ${colors.primary[300]}`,
            }}
          >
            <Box sx={{ width: 24, height: 24 }}>
              <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
            </Box>
            <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
              You can update, review, or manage settings on this screen. Changes will be applied instantly after save!
            </Typography>
          </Stack>
        </Stack>
      )}
      <Stack direction={"row"} alignItems={"center"} spacing={3}>
        <SaveButton disabled={loading} onClick={verify} label="Verify domain" />
        <CancelButton onClick={handleClose} />
      </Stack>
    </Stack>
  );
}

export default SSLConfigurationFree;
